import { useCallback, useEffect, useRef } from 'react';
import { EMPTY_STRING, HABIT_SETTING_FEATURES, ACTIVITY_TYPES } from 'constants/general';
import { ActivityType } from 'interfaces/common.interface';
import { useAppDispatch } from 'store/hooks';
import { updateSettingFeatures } from 'store/reducer/setting/slice';
import { getTimerModeToggleResult } from 'utils/habit-validation.util';
import { isNumber } from 'utils/number.util';

interface UseHabitRowTimerModeParams {
  readonly activity: ActivityType;
  readonly type: string;
  readonly position: number;
  readonly customRoutinePosition?: number;
}

const useHabitRowTimerMode = ({
  activity,
  type,
  position,
  customRoutinePosition
}: UseHabitRowTimerModeParams) => {
  const dispatch = useAppDispatch();
  const lastCompletionRef = useRef(
    activity.completion_requirements ?? EMPTY_STRING
  );
  const lastDurationRef = useRef(activity.duration_seconds ?? 0);
  const activityRef = useRef(activity);
  activityRef.current = activity;

  useEffect(() => {
    lastCompletionRef.current =
      activityRef.current.completion_requirements ?? EMPTY_STRING;
    lastDurationRef.current = activityRef.current.duration_seconds ?? 0;
  }, [activity.id]);

  const dispatchFeature = useCallback(
    (feature: string, value: string | number | undefined) => {
      dispatch(
        updateSettingFeatures({
          type,
          position,
          feature,
          value,
          customRoutinePosition
        })
      );
    },
    [customRoutinePosition, dispatch, position, type]
  );

  const handleDurationChange = useCallback(
    (totalSeconds: number | null) => {
      if (totalSeconds !== null) {
        lastDurationRef.current = totalSeconds;
      }
      dispatchFeature(
        HABIT_SETTING_FEATURES.DURATION,
        totalSeconds ?? EMPTY_STRING
      );
    },
    [dispatchFeature]
  );

  const handleCompletionChange = useCallback(
    (value: string) => {
      lastCompletionRef.current = value;
      dispatchFeature(HABIT_SETTING_FEATURES.COMPLETION_REQUIREMENTS, value);
    },
    [dispatchFeature]
  );

  const activityType = isNumber(customRoutinePosition)
    ? ACTIVITY_TYPES.CUSTOM_ROUTINES
    : type;

  const applyTimerToggle = useCallback(
    (useTimer: boolean) => {
      const result = getTimerModeToggleResult({
        useTimer,
        durationSeconds: activity.duration_seconds,
        completionRequirements: activity.completion_requirements,
        cachedCompletion: lastCompletionRef.current,
        cachedDuration: lastDurationRef.current,
        activityType
      });
      lastCompletionRef.current = result.cachedCompletion;
      lastDurationRef.current = result.cachedDuration;
      if (result.shouldUpdateDuration) {
        dispatchFeature(HABIT_SETTING_FEATURES.DURATION, result.nextDuration);
      }
      dispatchFeature(
        HABIT_SETTING_FEATURES.COMPLETION_REQUIREMENTS,
        result.nextCompletion
      );
    },
    [
      activity.completion_requirements,
      activity.duration_seconds,
      activityType,
      dispatchFeature
    ]
  );

  return {
    handleModeChange: applyTimerToggle,
    handleDurationChange,
    handleCompletionChange
  };
};

export default useHabitRowTimerMode;
